import type { FishState, ReefFish, World } from '../types';
import type { SiteId } from '../data/sites';
import type { Rng } from '../rng';
import { BULLETHEAD, EXPECTED, FISH_ISLANDS, KALA, MANINI, MOANOKEA, NENUE, OCTOPUS, OMILU, PALENOSE, REDLIP, ROI, TAAPE, ULUA, afloatFrom, type FishDraws, type FishSpeciesId, type IslandDay } from '../fish';
import { REEF_DIETS, stepReef, type EatenBy, type Hunt, type Predation, type ReefSpecies } from '../predation';
import { livingTagsAt, stepTaggedFish } from '../tags';
import { STREAM, stockStream } from '../streams';
import { deathAudit, type DeathAudit } from '../audit';
import { BULLETHEAD_JANUARY, BULLETHEAD_SUPPLY } from '../data/fish/bullethead';
import { PALENOSE_JANUARY, PALENOSE_SUPPLY } from '../data/fish/palenose';
import { ROI_JANUARY, ROI_SUPPLY } from '../data/fish/roi';
import { KALA_JANUARY, KALA_SUPPLY } from '../data/fish/kala';
import { ULUA_JANUARY, ULUA_SUPPLY } from '../data/fish/ulua';
import { OMILU_JANUARY, OMILU_SUPPLY } from '../data/fish/omilu';
import { REDLIP_JANUARY, REDLIP_SUPPLY } from '../data/fish/redlip';
import { OCTOPUS_JANUARY, OCTOPUS_SUPPLY } from '../data/fish/octopus';
import { NENUE_JANUARY, NENUE_SUPPLY } from '../data/fish/nenue';
import { MOANOKEA_JANUARY, MOANOKEA_SUPPLY } from '../data/fish/moanokea';
import { MANINI_JANUARY, MANINI_SUPPLY } from '../data/fish/manini';
import { TAAPE_JANUARY, TAAPE_SUPPLY } from '../data/fish/taape';
import { civilFromEpochDay, epochDayFromCivil, epochDayOf } from '../calendar';
import { islandFishing, openingManagement, type Management } from '../management';

/** Each reef fish the game has (`FISH_LIVES` order): its life, its balance's supply and first of January, and its streams' salt. */
export const REEF_SPECIES: readonly ReefSpecies[] = [
  { life: BULLETHEAD, supply: BULLETHEAD_SUPPLY, january: BULLETHEAD_JANUARY, salt: STREAM.bullethead },
  { life: PALENOSE, supply: PALENOSE_SUPPLY, january: PALENOSE_JANUARY, salt: STREAM.palenose },
  { life: ROI, supply: ROI_SUPPLY, january: ROI_JANUARY, salt: STREAM.roi },
  { life: KALA, supply: KALA_SUPPLY, january: KALA_JANUARY, salt: STREAM.kala },
  { life: ULUA, supply: ULUA_SUPPLY, january: ULUA_JANUARY, salt: STREAM.ulua },
  { life: OMILU, supply: OMILU_SUPPLY, january: OMILU_JANUARY, salt: STREAM.omilu },
  { life: REDLIP, supply: REDLIP_SUPPLY, january: REDLIP_JANUARY, salt: STREAM.redlip },
  { life: OCTOPUS, supply: OCTOPUS_SUPPLY, january: OCTOPUS_JANUARY, salt: STREAM.octopus },
  { life: NENUE, supply: NENUE_SUPPLY, january: NENUE_JANUARY, salt: STREAM.nenue },
  { life: MOANOKEA, supply: MOANOKEA_SUPPLY, january: MOANOKEA_JANUARY, salt: STREAM.moanokea },
  { life: MANINI, supply: MANINI_SUPPLY, january: MANINI_JANUARY, salt: STREAM.manini },
  { life: TAAPE, supply: TAAPE_SUPPLY, january: TAAPE_JANUARY, salt: STREAM.taape },
];

const speciesOf = (id: FishSpeciesId): ReefSpecies => REEF_SPECIES.find((s) => s.life.id === id) as ReefSpecies;

/** A stock's draws on its islands' streams of the day (M80, `stockStream`): binomial deaths on each class's count, a Poisson draw of settlers. */
function streamDraws(world: { seed: number; day: number }, siteId: SiteId, sp: ReefSpecies, streamOf: typeof stockStream = stockStream): FishDraws {
  const streams = FISH_ISLANDS.map((_, i) => streamOf(world, sp.salt, siteId, i));
  return { deaths: (n, p, i) => (streams[i] as Rng).binomial(n, p), settlers: (mean, i) => (streams[i] as Rng).count(mean) };
}

/** A death audit's report of a sanctuary's stocks (`audit.ts`). */
const toAudit = (audit: DeathAudit, siteId: SiteId, epochDay: number) => (sp: ReefSpecies, day: IslandDay, eatenBy: readonly EatenBy[], hunt: Hunt | undefined): void => {
  audit.stock({ ...day, species: sp.life.id, siteId, epochDay, eatenBy, ...(hunt ? { hunt } : {}) });
};

/**
 * Step 3 of the tick, the animals (M74): each sanctuary's reef fish die,
 * settle and spawn (`fish.ts`), species by species (M82), the roi eating
 * the parrotfish (M84), the kala beside them (M90), the jacks eating the
 * parrotfish too (M126), the redlip among the parrotfish (M128), the ulua the octopus (M129), the nenue beside them (M178), the jacks the moano kea (M179), their deaths binomial draws on each age class's count
 * and their settlers a Poisson draw on the day's expected settlers, each
 * species' island on its own stream of the day (M80, `stockStream`; a test
 * hands in its own as `streamOf`). With a death audit attached (`audit.ts`),
 * each island's day is told to it.
 */
export function stepAnimals(world: World, streamOf: typeof stockStream = stockStream): void {
  const epochDay = epochDayOf(world);
  const audit = deathAudit();
  for (const s of world.sanctuaries) {
    if (!s.fish) continue;
    const tagged = livingTagsAt(world, s.siteId) ? (hunts: readonly Predation[]) => stepTaggedFish(world, s.siteId, epochDay, hunts) : undefined;
    const management = s.management;
    stepReef(REEF_SPECIES, s.fish, epochDay, (sp) => streamDraws(world, s.siteId, sp, streamOf), audit ? toAudit(audit, s.siteId, epochDay) : undefined, REEF_DIETS, tagged,
      (sp) => islandFishing(sp, management, epochDay));
  }
}

/** Whether a site has reef fish: Maui Nui's four islands only, the stock Wren et al.'s exchange is measured for. */
export const siteHasFish = (siteId: SiteId): boolean => siteId === 'maui_nui';

/** Each species' stock on its balance's first of January of a year, its larvae afloat by their days to settle. */
function januaryFish(year: number): ReefFish {
  const first = epochDayFromCivil(year, 1, 1);
  const stock = (sp: ReefSpecies): FishState => ({ islands: sp.january.islands.map((c) => [...c]), afloat: afloatFrom(sp.life, sp.january.afloat, first) });
  return {
    bullethead: stock(speciesOf('bullethead')), palenose: stock(speciesOf('palenose')), roi: stock(speciesOf('roi')), kala: stock(speciesOf('kala')),
    ulua: stock(speciesOf('ulua')), omilu: stock(speciesOf('omilu')), redlip: stock(speciesOf('redlip')), octopus: stock(speciesOf('octopus')),
    nenue: stock(speciesOf('nenue')), moanokea: stock(speciesOf('moanokea')), manini: stock(speciesOf('manini')), taape: stock(speciesOf('taape')),
  };
}

/**
 * A sanctuary's reef fish as a world opens on a day: each species' balance's
 * first of January (`data/fish/`), stepped together at their expected values
 * through the opening year's days to the opening day, the roi and the jacks
 * eating the parrotfish and the ulua the octopus, fished under Hawaiʻi's rules (M170), each class's count then rounded
 * to a whole fish (no draw: a world's opening draws nothing).
 */
export function openingFish(epochDay: number, management: Management | null = openingManagement(true)): ReefFish {
  const year = civilFromEpochDay(epochDay).year;
  const fish = januaryFish(year);
  // Under Hawaiʻi's rules, as a world opens (M170); none given, at each stock's assessed fishing.
  for (let d = epochDayFromCivil(year, 1, 1); d <= epochDay; d++) stepReef(REEF_SPECIES, fish, d, () => EXPECTED, undefined, undefined, undefined, (sp) => islandFishing(sp, management, d));
  for (const sp of REEF_SPECIES) fish[sp.life.id].islands = fish[sp.life.id].islands.map((c) => c.map((n) => Math.round(n)));
  return fish;
}

/** A sanctuary's reef fish as a world opens on a day, where its site has them (`siteHasFish`); elsewhere none. */
export function sanctuaryOpeningFish(siteId: SiteId, epochDay: number): ReefFish | null {
  return siteHasFish(siteId) ? openingFish(epochDay) : null;
}
